import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList } from "@/mocks/anime";

const HOT_KEYWORDS = ["热血", "恋爱", "科幻", "经典", "星海旅人", "治愈"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return animeList.filter((anime) => {
      return (
        anime.title.toLowerCase().includes(q) ||
        anime.region.toLowerCase().includes(q) ||
        anime.description.toLowerCase().includes(q) ||
        anime.genres.some((genre) => genre.toLowerCase().includes(q))
      );
    });
  }, [query]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = keyword.trim();
    setSearchParams(next ? { q: next } : {});
  };

  return (
    <Layout>
      <Seo
        title={`${query ? `${query} - ` : ""}动漫搜索结果 - 樱花动漫`}
        description={`樱花动漫为您搜索「${query || "动漫"}」相关动画作品，支持按标题、题材、地区模糊匹配，快速找到想看的动漫。`}
        keywords={`${query || "动漫"}搜索,樱花动漫,动漫在线观看`}
        path="/search"
      />

      <div className="w-full px-4 pt-8 md:px-6 lg:px-10">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">搜索</span>
        </nav>

        <div className="mx-auto mt-6 max-w-3xl">
          <h1 className="text-center text-2xl font-bold text-foreground-950 md:text-3xl">搜索动漫</h1>
          <p className="mt-2 text-center text-sm text-foreground-500">输入作品名称、题材或地区，快速定位想看的动画</p>

          <form
            onSubmit={handleSubmit}
            className="mt-5 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors"
            role="search"
          >
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="例如：热血、恋爱、樱花之约…"
              aria-label="搜索动漫"
              className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setSearchParams({ q: word })}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>

        {query ? (
          <div className="mt-10">
            <p className="text-sm text-foreground-600">
              关键词「<span className="font-medium text-primary-600">{query}</span>」共找到
              <span className="font-medium text-primary-600"> {results.length} </span>部相关动漫
            </p>

            {results.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {results.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
                  <i className="ri-search-eye-line"></i>
                </span>
                <h2 className="mt-5 text-lg font-semibold text-foreground-950">未找到相关动漫</h2>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  换个关键词试试，或者看看热门分类里正在更新的作品。
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center justify-center rounded-lg bg-secondary-50 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
              <i className="ri-movie-2-line"></i>
            </span>
            <h2 className="mt-5 text-lg font-semibold text-foreground-950">开始你的探索</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-500">
              输入关键词，发现更多精彩的动漫作品。
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}